import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "../../../components/common/Badge";
import ActionButton from "../../../components/common/ActionButton";
import CashingUpReportPage from "./CashingUpReportPage";
import { useGetCashingUpVariancesReportQuery } from "../../../store/api";
import { formatDateTime } from "../../../utils/reporting";
import {
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const columns = [
  { key: "businessDate", label: "BUSINESS DATE" },
  { key: "tillName", label: "TILL" },
  { key: "expectedCash", label: "EXPECTED CASH" },
  { key: "actualCash", label: "ACTUAL CASH" },
  { key: "cashVariance", label: "VARIANCE" },
  { key: "status", label: "STATUS" },
  { key: "openedAt", label: "OPENED AT" },
  { key: "actions", label: "ACTIONS" },
];

const VariancesReport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <CashingUpReportPage
      title="Variances Report"
      subtitle="Review sessions where expected and actual cash differ."
      useQueryHook={useGetCashingUpVariancesReportQuery}
      collectionKeys={["variances", "items", "rows", "data"]}
      columns={columns}
      showBusinessDate={false}
      showDateRange
      showLimit={false}
      showExport={false}
      useSharedFilterBar
      buildRows={(collection) =>
        collection.map((session) => ({
          id: session.cashSessionId || `${session.storeId}-${session.businessDate}`,
          cashSessionId: session.cashSessionId || "-",
          businessDate: session.businessDate || "-",
          tillName: session.tillName || session.tillId || "-",
          statusValue: session.status || "",
          expectedCash: formatSignedCurrency(session.expectedCash),
          expectedCashValue: Number(session.expectedCash || 0),
          actualCash: formatSignedCurrency(session.actualCash),
          actualCashValue: Number(session.actualCash || 0),
          cashVarianceValue: Number(session.cashVariance || 0),
          cashVariance: (() => {
            const delta = Number(session.cashVariance || 0);
            const formatting = formatAmountWithImpact(delta);
            return (
              <span
                className={
                  formatting.tone === "rose"
                    ? "font-semibold text-rose-700"
                    : formatting.tone === "emerald"
                      ? "font-semibold text-emerald-700"
                      : "font-semibold text-slate-700"
                }
              >
                {formatting.value}
              </span>
            );
          })(),
          approvalStatusValue: session.approvalStatus || "",
          status: (
            <Badge tone={getStatusTone(session.status)}>
              {formatStatusLabel(session.status)}
            </Badge>
          ),
          approvalStatus: (
            <Badge tone={getStatusTone(session.approvalStatus)}>
              {formatStatusLabel(session.approvalStatus)}
            </Badge>
          ),
          syncStatus: (
            <Badge tone={getStatusTone(session.syncStatus)}>
              {formatStatusLabel(session.syncStatus)}
            </Badge>
          ),
          openedAt: formatDateTime(session.openedAt),
          actions: (
            <ActionButton
              label="View variance"
              icon="view"
              onClick={() =>
                navigate(
                  {
                    pathname: `/reporting/cashing-up/variances/${session.cashSessionId}`,
                    search: location.search,
                  },
                  { state: { session } },
                )
              }
            />
          ),
        }))
      }
      buildStats={(_, rows) => {
        const varianceValues = rows.map((row) => Number(row.cashVarianceValue || 0));
        const varianceCount = rows.length;
        const positiveVariance = varianceValues.filter((value) => value > 0).length;
        const negativeVariance = varianceValues.filter((value) => value < 0).length;
        const totalVariance = varianceValues.reduce((sum, value) => sum + value, 0);

        return [
          {
            label: "Variance Rows",
            value: String(varianceCount),
            helper: "Sessions returned by the report.",
            accent: "indigo",
          },
          {
            label: "Positive",
            value: String(positiveVariance),
            helper: "Rows where actual exceeded expected.",
            accent: "emerald",
          },
          {
            label: "Negative",
            value: String(negativeVariance),
            helper: "Rows where actual fell short.",
            accent: "rose",
          },
          {
            label: "Net Variance",
            value: formatSignedCurrency(totalVariance),
            helper: "Combined variance across rows.",
            accent: "amber",
          },
        ];
      }}
      emptyText="No variance records found for the selected store and date range."
    />
  );
};

export default VariancesReport;
